import { useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Phone,
  Package,
  ExternalLink,
} from "lucide-react";
import { SiteHeader, PHONE_DISPLAY, PHONE_HREF } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  PRODUCTS,
  PRODUCT_IMAGES,
  OFFICIAL_URLS,
  getCategoryLabel,
} from "@/data/products";
import { ProductVisual } from "@/pages/ProductsPage";

const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = PRODUCTS.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-100">
        <SiteHeader />
        <main className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6">
          <h1
            data-testid="product-not-found"
            className="font-display text-3xl font-extrabold text-navy-900"
          >
            Product not found
          </h1>
          <p className="mt-3 text-gray-600">
            The product you are looking for does not exist in our catalog.
          </p>
          <Link to="/products">
            <Button
              data-testid="back-to-products-button"
              className="mt-8 bg-navy-800 font-bold text-white hover:bg-navy-700"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to All Products
            </Button>
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const image = PRODUCT_IMAGES[product.slug];
  const officialUrl = OFFICIAL_URLS[product.slug];
  const related = PRODUCTS.filter(
    (p) => p.category === product.category && p.slug !== product.slug
  ).slice(0, 3);

  const specs = [
    ["Manufacturer", product.manufacturer],
    ["Category", getCategoryLabel(product.category)],
    ["Material", product.material],
    ["Applications", product.applications],
  ];

  return (
    <div data-testid="product-detail-page" className="min-h-screen bg-gray-100">
      <SiteHeader />

      <nav
        data-testid="detail-breadcrumb"
        className="border-b border-gray-200 bg-white"
      >
        <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-3 text-sm sm:px-6">
          <Link to="/products" className="font-semibold text-steel-500 hover:text-navy-800">
            All Products
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-600">{getCategoryLabel(product.category)}</span>
          <span className="text-gray-400">/</span>
          <span className="font-semibold text-navy-900">{product.name}</span>
        </div>
      </nav>

      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            {image ? (
              <img
                data-testid="detail-image"
                src={image}
                alt={product.name}
                className="h-72 w-full bg-white object-cover sm:h-96"
              />
            ) : (
              <div className="h-72 sm:h-96 [&>div]:h-full">
                <ProductVisual product={product} />
              </div>
            )}
          </div>

          <div>
            <span className="inline-block rounded-full bg-navy-800 px-3 py-1 text-xs font-bold text-white">
              {product.manufacturer}
            </span>
            <h1
              data-testid="detail-name"
              className="font-display mt-4 text-3xl font-extrabold text-navy-900 sm:text-4xl"
            >
              {product.name}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-gray-600">
              {product.blurb}
            </p>

            <div className="mt-8 overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="border-b border-gray-200 bg-gray-50 px-5 py-3">
                <h2 className="font-display text-sm font-bold uppercase tracking-widest text-navy-900">
                  Specifications
                </h2>
              </div>
              <dl>
                {specs.map(([label, value], i) => (
                  <div
                    key={label}
                    data-testid={`spec-${label.toLowerCase()}`}
                    className={`grid grid-cols-3 gap-4 px-5 py-3 text-sm ${
                      i % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                  >
                    <dt className="font-semibold text-navy-800">{label}</dt>
                    <dd className="col-span-2 text-gray-600">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <p className="mt-4 text-xs text-gray-500">
              For full technical data sheets, thicknesses, and system options,
              see the official manufacturer page or call us at {PHONE_DISPLAY}.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                data-testid="detail-quote-button"
                size="lg"
                onClick={() =>
                  navigate(`/?interest=${encodeURIComponent(product.name)}`)
                }
                className="bg-navy-800 px-8 font-bold text-white hover:bg-navy-700"
              >
                Request a Quote <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              {officialUrl && (
                <a href={officialUrl} target="_blank" rel="noopener noreferrer">
                  <Button
                    data-testid="detail-official-link"
                    size="lg"
                    variant="outline"
                    className="w-full border-navy-800 font-semibold text-navy-800 hover:bg-navy-800 hover:text-white sm:w-auto"
                  >
                    Official Product Page <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-16">
            <h2 className="font-display text-2xl font-extrabold text-navy-900">
              More {getCategoryLabel(product.category)}
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/products/${r.slug}`}
                  data-testid={`related-${r.slug}`}
                >
                  <Card className="flex h-full items-center gap-4 border-gray-200 bg-white p-4 transition-shadow hover:shadow-lg">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md bg-gray-100">
                      {PRODUCT_IMAGES[r.slug] ? (
                        <img
                          src={PRODUCT_IMAGES[r.slug]}
                          alt={r.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-navy-800">
                          <Package className="h-6 w-6 text-steel-300" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-0">
                      <p className="font-display text-sm font-bold text-navy-900">
                        {r.name}
                      </p>
                      <p className="text-xs text-gray-500">{r.manufacturer}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <section className="bg-navy-900 py-12">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 md:flex-row md:items-center">
          <div>
            <h2 className="font-display text-2xl font-extrabold text-white">
              Questions about {product.name}?
            </h2>
            <p className="mt-2 max-w-xl text-base text-navy-100">
              Call our team for current availability, system pairing, and
              project pricing.
            </p>
          </div>
          <a href={PHONE_HREF}>
            <Button
              data-testid="detail-call-button"
              size="lg"
              className="bg-white px-8 font-bold text-navy-900 hover:bg-navy-100"
            >
              <Phone className="mr-2 h-4 w-4" /> {PHONE_DISPLAY}
            </Button>
          </a>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default ProductDetailPage;
